import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import Button from '../Button/Button';
import Dialog from '../Dialog/Dialog';

import styles from './ThumbnailUserPickerModal.module.scss';

type CoverType = 'reel' | 'video';

type Frame = {
  url: string;
  time: number;
};

type Props = {
  open: boolean;
  onClose: () => void;
  videoFile: File | string | null;
  onSave: (thumbnail: string | File) => void;
  initialThumbnail?: string;
  type?: CoverType;
};

const FILMSTRIP_QUALITY = 0.92;
const FINAL_QUALITY = 0.95;

const ThumbnailUserPickerModal = ({ open, onClose, videoFile, onSave, initialThumbnail, type }: Props) => {
  const { t } = useTranslation('upload');
  const [frames, setFrames] = useState<Frame[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [detectedType, setDetectedType] = useState<CoverType>('reel');
  const touchStartX = useRef<number | null>(null);
  const isScrubbing = useRef(false);
  const stripRef = useRef<HTMLDivElement>(null);

  const coverType: CoverType = type ?? detectedType;

  useEffect(() => {
    if (open && videoFile) {
      setFrames([]);
      const frameCount = window.innerWidth <= 768 ? 8 : 12;
      extractFrames(videoFile, frameCount);
    }
  }, [open, videoFile]);

  useEffect(() => {
    if (!open) {
      setFrames([]);
    }
    setSelectedIndex(0);
  }, [open]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open || frames.length === 0) { return; }

      if (e.key === 'ArrowLeft') {
        setSelectedIndex((prev) => Math.max(0, prev - 1));
      } else if (e.key === 'ArrowRight') {
        setSelectedIndex((prev) => Math.min(frames.length - 1, prev + 1));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, frames.length]);

  const extractFrames = async (fileOrUrl: File | string, count: number) => {
    setLoading(true);
    let videoObjectUrl: string | null = null;

    try {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.playsInline = true;

      let videoBlob: Blob;
      if (typeof fileOrUrl === 'string') {
        const response = await fetch(fileOrUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch video: ${response.statusText}`);
        }
        videoBlob = await response.blob();
        videoObjectUrl = URL.createObjectURL(videoBlob);
        video.src = videoObjectUrl;
      } else {
        videoObjectUrl = URL.createObjectURL(fileOrUrl);
        video.src = videoObjectUrl;
      }

      await new Promise((resolve, reject) => {
        video.onloadedmetadata = () => resolve(null);
        video.onerror = () => reject(new Error('Failed to load video metadata'));
      });

      const isLandscape = video.videoWidth >= video.videoHeight;
      setDetectedType(isLandscape ? 'video' : 'reel');

      const duration = video.duration;
      if (!Number.isFinite(duration) || duration <= 0) {
        throw new Error('Invalid video duration');
      }

      const interval = duration / (count + 1);
      const extractedFrames: Frame[] = [];

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('Failed to get canvas context');
      }

      for (let i = 1; i <= count; i++) {
        const time = i * interval;
        video.currentTime = time;

        await new Promise((resolve, reject) => {
          const handleSeeked = () => {
            try {
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              extractedFrames.push({
                url: canvas.toDataURL('image/jpeg', FILMSTRIP_QUALITY ?? 0.7),
                time
              });
              resolve(null);
            } catch (canvasError) {
              reject(canvasError);
            }
          };

          video.onseeked = handleSeeked;
          video.onerror = () => reject(new Error(`Failed seeking video at ${time}s`));
        });
      }

      setFrames(extractedFrames);
    } catch (error) {
      console.error('Error extracting frames:', error);
    } finally {
      if (videoObjectUrl) {
        URL.revokeObjectURL(videoObjectUrl);
      }
      setLoading(false);
    }
  };

  const captureHighQualityFrame = (fileOrUrl: File | string, time: number): Promise<File> =>
    new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;

      const videoUrl = typeof fileOrUrl === 'string' ? fileOrUrl : URL.createObjectURL(fileOrUrl);
      video.src = videoUrl;

      video.onloadedmetadata = () => {
        video.currentTime = time;
      };

      video.onseeked = () => {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(
          (blob) => {
            if (typeof fileOrUrl !== 'string') URL.revokeObjectURL(video.src);
            if (!blob) {
              reject(new Error('capture_failed'));
              return;
            }
            resolve(new File([blob], 'thumbnail.jpg', { type: 'image/jpeg' }));
          },
          'image/jpeg',
          FINAL_QUALITY
        );
      };

      video.onerror = () => {
        if (typeof fileOrUrl !== 'string') URL.revokeObjectURL(video.src);
        reject(new Error('video_load_failed'));
      };
    });

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) { return; }

    const touchEndX = e.changedTouches[0].clientX;
    const diffX = touchEndX - touchStartX.current;

    if (Math.abs(diffX) > 50) {
      if (diffX > 0) {
        setSelectedIndex((prev) => Math.max(0, prev - 1));
      } else {
        setSelectedIndex((prev) => Math.min(frames.length - 1, prev + 1));
      }
    }
    touchStartX.current = null;
  };

  // ✅ Clean Scrubbing handler
  const handleScrub = useCallback((clientX: number, clientY: number) => {
    const element = document.elementFromPoint(clientX, clientY);
    const thumb = element?.closest(`.${styles.frameThumb}`);

    if (thumb instanceof HTMLElement) {
      const index = Array.from(stripRef.current?.children ?? []).indexOf(thumb);

      if (index !== -1 && index !== selectedIndex) {
        setSelectedIndex(index);
      }
    }
  }, [selectedIndex]);

  const handleMouseDown = (e: React.MouseEvent) => {
    isScrubbing.current = true;
    handleScrub(e.clientX, e.clientY);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isScrubbing.current) {
      handleScrub(e.clientX, e.clientY);
    }
  };

  const handleTouchScrub = (e: React.TouchEvent) => {
    e.preventDefault();
    isScrubbing.current = true;
    const touch = e.touches[0];
    handleScrub(touch.clientX, touch.clientY);
  };

  const handleTouchMoveScrub = (e: React.TouchEvent) => {
    e.preventDefault();
    if (isScrubbing.current) {
      const touch = e.touches[0];
      handleScrub(touch.clientX, touch.clientY);
    }
  };

  const endScrubbing = () => {
    isScrubbing.current = false;
  };

  const handleSave = async () => {
    const selected = frames[selectedIndex];

    if (!selected || !videoFile) {
      onClose();
      return;
    }

    setIsSaving(true);
    try {
      const highQualityFile = await captureHighQualityFrame(videoFile, selected.time);
      onSave(highQualityFile);
    } catch (error) {
      console.error('Error capturing final thumbnail:', error);
      onSave(selected.url);
    } finally {
      setIsSaving(false);
      onClose();
    }
  };

  const currentPreview = frames[selectedIndex]?.url ?? (loading ? undefined : initialThumbnail);

  return (
    <Dialog size="large" open={open} onClose={onClose} role="dialog" shouldNotCloseButton={true}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.title}>{t('thumbnail_picker.title')}</h2>
        </div>

        <div className={styles.body}>
          <div
            className={classNames(styles.previewContainer, styles[coverType])}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {currentPreview ? (
              <img
                src={currentPreview}
                alt="Cover Preview"
                className={styles.mainPreview}
              />
            ) : (
              <div className={styles.placeholder} />
            )}
          </div>

          <div
            ref={stripRef}
            className={styles.frameStrip}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={endScrubbing}
            onMouseLeave={endScrubbing}
            onTouchStart={handleTouchScrub}
            onTouchMove={handleTouchMoveScrub}
            onTouchEnd={endScrubbing}
            onTouchCancel={endScrubbing}
          >
            {loading ? (
              <div className={styles.loadingFrames}>{t('thumbnail_picker.loading_frames')}</div>
            ) : (
              frames.map((frame, index) => (
                <div
                  key={frame.time}
                  // ✅ Fix: Kept `styles.frameThumb` base class for both reel and video so scrubbing works smoothly
                  className={classNames(styles.frameThumb, styles[coverType], { [styles.selected]: selectedIndex === index })}
                >
                  <img src={frame.url} alt={`Frame ${index}`} />
                </div>
              ))
            )}
          </div>
        </div>

        <div className={styles.footer}>
          <Button
            label={t('thumbnail_picker.cancel')}
            onClick={onClose}
            variant="mediaOverlay"
            color="secondary"
            className={styles.cancelBtn}
            disabled={isSaving}
          />
          <Button
            label={isSaving ? t('thumbnail_picker.saving') : t('thumbnail_picker.save')}
            onClick={handleSave}
            variant="contained"
            color="primary"
            className={styles.saveBtn}
            disabled={isSaving || frames.length === 0}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default ThumbnailUserPickerModal;